import {
  BotIcon,
  KeyRoundIcon,
  RefreshCwIcon,
  ScaleIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  ShieldOffIcon,
  TriangleAlertIcon,
} from 'lucide-react'
import { useState, type FormEvent } from 'react'

import { errorMessage } from '@/api/client'
import {
  useClearPoliceApiKey,
  useConnections,
  useDesignatePolice,
  useIsZoneAdministrator,
  useMe,
  usePolice,
  useResignPolice,
  usePoliceModels,
  useUpdatePolice,
  useVerifyPoliceApiKey,
} from '@/api/queries'
import type {
  ConnectionSummary,
  PoliceEffort,
  PoliceSettings,
  SupervisionMode,
  UpdatePoliceRequest,
  ZoneSummary,
} from '@/api/types'
import { PageBody, PageHeader } from '@/components/AppShell'
import { PausedZoneNotice, RequireZone } from '@/components/RequireZone'
import { EmptyState, ErrorState } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { inspectApiKey, storedKeyLooksTruncated } from '@/lib/apiKey'
import { formatAbsolute, formatMicros, formatRelative } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useReadOnly } from '@/staff/hooks'

/**
 * §19's confirmation copy, in one place because it is the thing the section is
 * actually about. Designating an agent moves supervision off the platform and
 * onto the zone administrator personally; the server refuses the designation
 * without `acknowledged: true`, so this modal is the only place that flag is
 * ever sent from.
 */
const ACKNOWLEDGEMENT = 'I know what I am doing'

/*
 * The model list is no longer hard-coded here.
 *
 * It comes from `GET .../police/models`, because the server now VALIDATES the choice against the
 * enabled catalogue — a code that is not an enabled row is refused. A free-text field offering
 * suggestions would let an administrator type something the server will reject, and would go stale
 * the moment a model is added or retired.
 */

/**
 * How the console reports what verification found.
 *
 * REJECTED is the careful one. saltseed can only ask api.anthropic.com, so a gateway or router
 * token comes back refused exactly like a truncated key — calling it "invalid" would be a
 * confident lie told to a customer whose setup is correct. The wording therefore reports what
 * happened and names the innocent explanation first, and only leans hard when the key is ALSO too
 * short to have a prefix, which is the combination that really does mean a broken paste.
 */
function apiKeyVerdict(
  settings: PoliceSettings,
): { variant: 'info' | 'warning' | 'destructive'; title: string; detail: string } | undefined {
  if (!settings.has_api_key) return undefined
  switch (settings.api_key_status) {
    case 'VERIFIED':
      return undefined
    case 'REJECTED':
      return storedKeyLooksTruncated(settings.has_api_key, settings.api_key_prefix)
        ? {
            variant: 'destructive',
            title: 'Anthropic refused this key, and it looks truncated',
            detail:
              'It is too short for saltseed to show even a prefix, and Anthropic rejected it. ' +
              'That combination almost always means a partial paste. Policing will not work until ' +
              'it is replaced.',
          }
        : {
            variant: 'warning',
            title: 'Anthropic did not accept this key',
            detail:
              'saltseed can only check keys against api.anthropic.com. If this is a gateway or ' +
              'proxy token — LiteLLM, OpenRouter, something internal — that result is expected and ' +
              'nothing is wrong. If it is meant to be an Anthropic key, it has been revoked or ' +
              'mistyped.',
          }
    case 'UNREACHABLE':
      return {
        variant: 'info',
        title: 'Could not check this key',
        detail:
          'saltseed could not reach the model API — a timeout, blocked egress, or a bad minute at ' +
          'the other end. This says nothing about the key itself.',
      }
    default:
      return {
        variant: 'info',
        title: 'This key has not been checked',
        detail: 'Verify it now, rather than finding out when policing is needed.',
      }
  }
}


/* -------------------------------------------------------------------------- */
/* Seat summary                                                               */
/* -------------------------------------------------------------------------- */

function SeatBanner({ settings }: { settings: PoliceSettings }) {
  if (settings.delegated) {
    return (
      <Alert variant="warning">
        <TriangleAlertIcon />
        <AlertTitle>
          saltseed is not supervising this zone —{' '}
          <span className="font-mono">{settings.designated_connection_name ?? 'an agent'}</span> is.
        </AlertTitle>
        <AlertDescription>
          A connected agent holds the police seat. saltseed judges nothing here: what gets paused,
          and why, is entirely that agent&apos;s decision and your responsibility. Hand the seat
          back to saltseed with <span className="text-foreground">Return the seat</span> below.
          {settings.designated_at === null ? null : (
            <> Designated {formatRelative(settings.designated_at)}.</>
          )}
        </AlertDescription>
      </Alert>
    )
  }

  if (!settings.enabled) {
    return (
      <Alert>
        <ShieldOffIcon />
        <AlertTitle>Policing is off in this zone.</AlertTitle>
        <AlertDescription>
          Nothing is supervising the traffic here and no connection can pause an agent except you,
          by hand, from the Connections page. Switch it on below to have saltseed watch the zone
          against the prompt, or designate one of your own connected agents to do the watching.
        </AlertDescription>
      </Alert>
    )
  }

  if (!settings.has_api_key) {
    return (
      <Alert variant="warning">
        <TriangleAlertIcon />
        <AlertTitle>Policing is on, but there is no Claude API key.</AlertTitle>
        <AlertDescription>
          saltseed cannot evaluate this zone&apos;s traffic without a key to call the model with.
          Set one below, or designate a connected agent as the police instead.
        </AlertDescription>
      </Alert>
    )
  }

  // The managed evaluator does not exist yet: nothing in the backend decrypts the
  // stored key, there is no Claude client, and `PoliceService` calls it "the future
  // managed evaluator". Saying "saltseed is supervising this zone" would tell an
  // administrator they can stop watching, which is the one thing this screen must
  // not do. State what is true, and name what does stop an agent today.
  return (
    <Alert variant="warning">
      <ShieldCheckIcon />
      <AlertTitle>Stored, but saltseed is not evaluating traffic yet.</AlertTitle>
      <AlertDescription>
        The key and the prompt below are kept for the managed police, which is not live:{' '}
        <span className="font-mono">{settings.model}</span> is not reading this zone&apos;s
        messages. Today an agent is only stopped by a police agent you designate below, or by a
        manual pause from the Connections page — and a pause always tells the agent why, and
        always emails this zone&apos;s administrator.
      </AlertDescription>
    </Alert>
  )
}

/* -------------------------------------------------------------------------- */
/* Configuration                                                              */
/* -------------------------------------------------------------------------- */

function ConfigurationCard({
  zone,
  settings,
  frozen,
}: {
  zone: ZoneSummary
  settings: PoliceSettings
  /**
   * True when the zone is not `reachable` (§17). Every police write resolves
   * through `ZoneAccess.reachableAsZoneAdministrator`, so in a deactivated or
   * archived zone — or any zone of a non-ACTIVE account — a save answers 404.
   * The GET stays readable, which is why the form renders at all; letting the
   * buttons issue a request that can only 404 would look like a broken console
   * rather than a lifecycle rule.
   */
  frozen: boolean
}) {
  const update = useUpdatePolice()
  const clearKey = useClearPoliceApiKey()

  const [prompt, setPrompt] = useState(settings.prompt)
  const [model, setModel] = useState(settings.model)
  const [effort, setEffort] = useState<PoliceEffort>(settings.effort)
  const [supervisionMode, setSupervisionMode] = useState<SupervisionMode>(settings.supervision_mode)
  const [confirmingGate, setConfirmingGate] = useState(false)
  const models = usePoliceModels(settings.zone_id)
  const selectedModel = models.data?.find((option) => option.code === model)
  // Write-only. §19: the server has no route that returns the plaintext, so this
  // field starts empty on every render and an empty submit means "leave the
  // stored key alone" — never "clear it". Clearing is the explicit button.
  const [apiKey, setApiKey] = useState('')
  // What the server said the last time we seeded, PER FIELD — not a timestamp.
  const [loaded, setLoaded] = useState({ prompt: settings.prompt, model: settings.model })

  // Re-seed the form when the server's copy moves under us (another tab, the
  // designate flow switching `enabled` on), but never while the user is typing
  // into a field whose stored value has not changed.
  //
  // Keying this on `updated_at` did the opposite of what the comment claimed:
  // ANY write bumped the timestamp, so clicking "Turn policing on" — a separate
  // mutation that sends only `{enabled}` — silently overwrote a rewritten
  // 3000-character prompt with the stored one, no warning and no undo. A field is
  // now re-seeded only when the SERVER's value for it changed AND the local value
  // is still the one we seeded, so an in-progress edit always wins.
  if (loaded.prompt !== settings.prompt || loaded.model !== settings.model) {
    if (loaded.prompt !== settings.prompt && prompt === loaded.prompt) setPrompt(settings.prompt)
    if (loaded.model !== settings.model && model === loaded.model) setModel(settings.model)
    setLoaded({ prompt: settings.prompt, model: settings.model })
  }

  const dirty =
    prompt !== settings.prompt ||
    model !== settings.model ||
    effort !== settings.effort ||
    supervisionMode !== settings.supervision_mode ||
    apiKey.trim().length > 0
  const keyConcern = inspectApiKey(apiKey)
  const verdict = apiKeyVerdict(settings)
  const verifyKey = useVerifyPoliceApiKey()

  function submit(event: FormEvent) {
    event.preventDefault()
    const body: UpdatePoliceRequest = {}
    if (prompt !== settings.prompt) body.prompt = prompt
    if (model !== settings.model) body.model = model
    if (effort !== settings.effort) body.effort = effort
    if (supervisionMode !== settings.supervision_mode) body.supervision_mode = supervisionMode
    // Only sent when actually typed. An absent `api_key` leaves the stored one
    // in place — that is the contract, and it is why this field is never seeded.
    if (apiKey.trim().length > 0) body.api_key = apiKey.trim()
    update.mutate(
      { zoneId: zone.id, body },
      {
        onSuccess: () => {
          setApiKey('')
        },
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ScaleIcon className="size-4 text-primary" />
          What the police is told to look for
        </CardTitle>
        <CardDescription>
          The prompt saltseed evaluates this zone&apos;s traffic against. It arrives prefilled with
          a working supervision policy — edit it to match what this zone is actually for.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-6">
          <div className="space-y-1.5">
            <Label htmlFor="police-prompt">Supervision prompt</Label>
            <Textarea
              id="police-prompt"
              value={prompt}
              onChange={(event) => {
                setPrompt(event.target.value)
              }}
              rows={16}
              maxLength={8000}
              className="font-mono text-xs"
              disabled={settings.delegated || frozen}
            />
            <p className="text-xs text-muted-foreground tabular">
              {prompt.length} / 8000 characters
              {settings.delegated
                ? ' — unused while a connected agent holds the seat.'
                : null}
            </p>
          </div>

          {/*
            Supervision mode. Off by default and warned about in red, because turning it on puts a
            model call on the critical path of every message: seconds of latency AND a charge per
            message. The confirmation restates the fail-closed behaviour, which is the part nobody
            expects and the part that pages somebody at 3am.
          */}
          <div className="space-y-3 rounded-md border border-destructive/40 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="police-gate" className="text-sm font-medium">
                  Evaluate every message before it is sent
                </Label>
                <p className="text-destructive text-xs font-semibold">
                  Not recommended — this will slow down processing.
                </p>
              </div>
              <input
                id="police-gate"
                type="checkbox"
                className="mt-1 size-4 accent-destructive"
                /*
                  Checked while the confirmation is open, not only once it is accepted.
                  Without the `|| confirmingGate` the box springs back the instant it is clicked --
                  the state it is bound to has not changed yet -- and the control reads as broken
                  rather than as cautious. Cancelling puts it back; confirming keeps it.
                */
                checked={supervisionMode === 'GATED' || confirmingGate}
                disabled={frozen}
                onChange={(event) => {
                  if (event.target.checked) {
                    setConfirmingGate(true)
                  } else {
                    setSupervisionMode('ASYNC')
                  }
                }}
              />
            </div>

            <p className="text-xs text-muted-foreground">
              By default the police analyses conversations <strong>after</strong> they happen and
              never delays a message; when it finds abuse or dangerous work it stops the agent
              responsible and tells it to cease operations. Turning this on instead holds{' '}
              <strong>every</strong> message until it has been reviewed.
            </p>

            {supervisionMode === 'GATED' && selectedModel ? (
              <Alert variant="destructive">
                <AlertTitle>Every message waits for a review, and is charged for one</AlertTitle>
                <AlertDescription>
                  About {formatMicros(selectedModel.estimated_micros_per_evaluation)} per message —
                  roughly{' '}
                  {formatMicros(selectedModel.estimated_micros_per_evaluation * 1000)} per 1,000
                  messages. If the police cannot evaluate a message, that message is{' '}
                  <strong>not delivered</strong>.
                </AlertDescription>
              </Alert>
            ) : null}
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="police-model">Model</Label>
              <select
                id="police-model"
                value={model}
                onChange={(event) => {
                  setModel(event.target.value)
                }}
                disabled={frozen}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs disabled:opacity-50"
              >
                {models.data?.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.display_name} — about {formatMicros(option.estimated_micros_per_evaluation)}{' '}
                    per evaluation
                  </option>
                ))}
                {/* The stored value may be a code no longer offered — a retired model. Keep it
                    selectable so opening the form does not silently change the zone's model. */}
                {models.data && !models.data.some((option) => option.code === model) ? (
                  <option value={model}>{model} (no longer offered)</option>
                ) : null}
              </select>
              <p className="text-xs text-muted-foreground">
                The police reads this zone&apos;s traffic with this model. Cost is per evaluation,
                against your balance, and the spread across the list is about five-fold.
              </p>

              {/*
                Shown on SELECTION, before saving. A rejected request discovered in production
                looks like an outage, and the cause — an account-level retention setting nobody on
                this screen can see — is not one anybody guesses.
              */}
              {selectedModel?.requires_data_retention ? (
                <Alert variant="destructive">
                  <AlertTitle>{selectedModel.display_name} requires 30-day data retention</AlertTitle>
                  <AlertDescription>
                    It is not available to organisations configured for zero data retention, and
                    requests will be rejected. It is also the most expensive option here — about{' '}
                    {formatMicros(selectedModel.estimated_micros_per_evaluation)} per evaluation.
                  </AlertDescription>
                </Alert>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="police-effort">Reasoning effort</Label>
              <select
                id="police-effort"
                value={effort}
                onChange={(event) => {
                  setEffort(event.target.value as PoliceEffort)
                }}
                disabled={frozen}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs disabled:opacity-50"
              >
                <option value="LOW">Low — recommended</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
              </select>
              <p className="text-xs text-muted-foreground">
                How hard the model thinks before answering. Deciding whether a transcript breaches
                your rules rarely needs more than <strong>Low</strong>, and thinking is billed as
                output — the most expensive kind of token.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="police-api-key">Claude API key</Label>
              <Input
                id="police-api-key"
                type="password"
                value={apiKey}
                onChange={(event) => {
                  setApiKey(event.target.value)
                }}
                placeholder={settings.has_api_key ? 'Replace the stored key' : 'sk-ant-…'}
                autoComplete="off"
                maxLength={512}
                disabled={frozen}
              />

              {/* D16: advice, never a block. The save proceeds either way — see
                  lib/apiKey.ts for why this is deliberately narrow. */}
              {keyConcern === undefined ? null : (
                <Alert variant="warning" className="mt-2">
                  <TriangleAlertIcon aria-hidden className="size-4" />
                  <AlertTitle>{keyConcern.title}</AlertTitle>
                  <AlertDescription>{keyConcern.detail}</AlertDescription>
                </Alert>
              )}

              {/* The stored key is never sent back, so the console cannot inspect it.
                  It does not need to: the server withholds the prefix below 16
                  characters, so "set, but no prefix" is itself the signal. */}
              {apiKey.trim().length === 0 &&
              storedKeyLooksTruncated(settings.has_api_key, settings.api_key_prefix) ? (
                <Alert variant="warning" className="mt-2">
                  <TriangleAlertIcon aria-hidden className="size-4" />
                  <AlertTitle>The stored key looks truncated</AlertTitle>
                  <AlertDescription>
                    It is too short for saltseed to show you even a prefix of it, which usually
                    means a partial paste. Policing will not work until it is replaced, and it
                    will fail without telling you. Paste the whole key above to replace it.
                  </AlertDescription>
                </Alert>
              ) : null}

              {apiKey.trim().length === 0 && verdict !== undefined ? (
                <Alert variant={verdict.variant} className="mt-2">
                  <TriangleAlertIcon aria-hidden className="size-4" />
                  <AlertTitle>{verdict.title}</AlertTitle>
                  <AlertDescription>{verdict.detail}</AlertDescription>
                </Alert>
              ) : null}

              {settings.has_api_key ? (
                <div className="flex flex-wrap items-center gap-2 pt-0.5">
                  <Badge
                    variant={settings.api_key_status === 'VERIFIED' ? 'success' : 'outline'}
                    className="gap-1.5"
                  >
                    <KeyRoundIcon className="size-3" />
                    {settings.api_key_status === 'VERIFIED' ? 'Key verified' : 'Key set'}
                  </Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={verifyKey.isPending || frozen}
                    onClick={() => {
                      verifyKey.mutate({ zoneId: zone.id })
                    }}
                  >
                    <RefreshCwIcon
                      className={cn('size-3.5', verifyKey.isPending ? 'animate-spin' : undefined)}
                    />
                    {verifyKey.isPending ? 'Checking…' : 'Re-check'}
                  </Button>
                  <span
                    className="font-mono text-xs text-muted-foreground"
                    title={
                      settings.api_key_set_at === null
                        ? undefined
                        : formatAbsolute(settings.api_key_set_at)
                    }
                  >
                    {settings.api_key_prefix ?? '—'}…
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={clearKey.isPending || frozen}
                    onClick={() => {
                      clearKey.mutate({ zoneId: zone.id })
                    }}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    {clearKey.isPending ? 'Removing…' : 'Remove key'}
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No key stored.</p>
              )}
              <p className="text-xs text-muted-foreground">
                Stored encrypted and never returned — not by this page, not by any endpoint. Only
                the first few characters come back, so you can tell which key is in place. Leave
                this blank to keep the stored one.
              </p>
            </div>
          </div>

          {update.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not save the police settings</AlertTitle>
              <AlertDescription>{errorMessage(update.error)}</AlertDescription>
            </Alert>
          ) : null}
          {clearKey.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not remove the key</AlertTitle>
              <AlertDescription>{errorMessage(clearKey.error)}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={!dirty || update.isPending || frozen}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              variant={settings.enabled ? 'outline' : 'default'}
              disabled={update.isPending || frozen}
              onClick={() => {
                update.mutate({ zoneId: zone.id, body: { enabled: !settings.enabled } })
              }}
            >
              {settings.enabled ? (
                <>
                  <ShieldOffIcon className="size-4" />
                  Turn policing off
                </>
              ) : (
                <>
                  <ShieldCheckIcon className="size-4" />
                  Turn policing on
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>

      {/*
        The confirmation restates the two consequences the checkbox label cannot fit, and the
        second one is the surprise: when the police cannot evaluate, messages STOP. An administrator
        who learns that from an outage rather than from this dialog has been failed by the dialog.
      */}
      <AlertDialog open={confirmingGate} onOpenChange={setConfirmingGate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Evaluate every message before sending it?</AlertDialogTitle>
            <AlertDialogDescription>
              Every message in this zone will wait for a police review before it is delivered,
              adding seconds to each one.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <Alert variant="destructive">
            <AlertTitle>If the police cannot evaluate a message, it is not delivered</AlertTitle>
            <AlertDescription>
              That is what this setting means: no unreviewed message goes out. A balance that runs
              out, or a model that cannot be reached, stops the conversation.
              {selectedModel
                ? ` It also costs about ${formatMicros(selectedModel.estimated_micros_per_evaluation)} per message.`
                : null}
            </AlertDescription>
          </Alert>

          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setSupervisionMode('ASYNC')
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setSupervisionMode('GATED')
                setConfirmingGate(false)
              }}
            >
              Yes, gate every message
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}

/* -------------------------------------------------------------------------- */
/* Designation — the modal is the requirement                                 */
/* -------------------------------------------------------------------------- */

function DesignateDialog({
  zone,
  target,
  onClose,
}: {
  zone: ZoneSummary
  target: ConnectionSummary | undefined
  onClose: () => void
}) {
  const designate = useDesignatePolice()

  function close() {
    designate.reset()
    onClose()
  }

  return (
    <Dialog
      open={target !== undefined}
      onOpenChange={(next) => {
        if (!next) close()
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TriangleAlertIcon className="size-5 text-destructive" />
            Make <span className="font-mono">{target?.name ?? 'this agent'}</span> the police of{' '}
            <span className="font-mono">{zone.name}</span>?
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-3 pt-1">
              <p className="text-foreground">
                <strong>saltseed stops supervising this zone.</strong> From the moment you confirm,
                nothing on the platform evaluates the traffic here — not the prompt you wrote, not
                the model you picked. Neither is consulted again while this agent holds the seat.
              </p>
              <p>
                <strong className="text-foreground">
                  Policing becomes your own responsibility.
                </strong>{' '}
                That agent decides, on its own judgement, which agents in this zone get paused and
                why. It can pause any connection here at any time, and every pause it makes is
                delivered to the agent and emailed to this zone&apos;s administrator as though you
                had made it yourself. If it pauses the wrong thing, or fails to pause the right
                thing, that is on you and not on saltseed.
              </p>
              <p>
                Exactly one connection holds the seat at a time. You can take it back at any point
                with <span className="text-foreground">Return the seat</span>, and saltseed picks
                supervision back up where it left off.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        {designate.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not designate that agent</AlertTitle>
            <AlertDescription>{errorMessage(designate.error)}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={close} disabled={designate.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={designate.isPending}
            onClick={() => {
              if (target === undefined) return
              designate.mutate(
                { zoneId: zone.id, connectionId: target.id },
                { onSuccess: close },
              )
            }}
          >
            {designate.isPending ? 'Handing over…' : ACKNOWLEDGEMENT}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DelegationCard({
  zone,
  settings,
  frozen,
}: {
  zone: ZoneSummary
  settings: PoliceSettings
  frozen: boolean
}) {
  const connections = useConnections(zone.id)
  const resign = useResignPolice()
  const [candidate, setCandidate] = useState<ConnectionSummary | undefined>(undefined)

  // §19: "the zone administrator promotes a connected AGENT". A human presence has
  // no MCP session and could therefore never call `pause_agent` — designating one
  // fills the seat with something structurally incapable of policing while this
  // page announces the zone as supervised. The server refuses it too (409, "Not an
  // agent"); this filter is so the console never offers what the server will reject.
  const eligible = (connections.data ?? []).filter(
    (row) =>
      row.type === 'agent' &&
      row.state === 'CONNECTED' &&
      !row.paused &&
      row.id !== settings.designated_connection_id,
  )

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BotIcon className="size-4 text-primary" />
            Or let one of your own agents do it
          </CardTitle>
          <CardDescription>
            A connected agent can hold the police seat instead of saltseed. Exactly one connection
            at a time, and only while it stays connected — if it disconnects, the seat is free
            again.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {settings.delegated ? (
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-[color-mix(in_oklch,var(--destructive)_35%,var(--border))] bg-[color-mix(in_oklch,var(--destructive)_8%,var(--card))] px-4 py-3.5">
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <ShieldAlertIcon className="size-4 text-destructive" />
                  <span className="font-mono">
                    {settings.designated_connection_name ?? 'A disconnected agent'}
                  </span>{' '}
                  holds the seat
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {settings.designated_connection_name === null
                    ? 'It is no longer connected, so nothing is policing this zone. Return the seat to hand supervision back to saltseed.'
                    : 'saltseed is not judging anything in this zone while this is in force.'}
                </p>
              </div>
              <Button
                variant="outline"
                disabled={resign.isPending || frozen}
                onClick={() => {
                  resign.mutate({ zoneId: zone.id })
                }}
              >
                {resign.isPending ? 'Returning…' : 'Return the seat'}
              </Button>
            </div>
          ) : null}

          {resign.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not return the seat</AlertTitle>
              <AlertDescription>{errorMessage(resign.error)}</AlertDescription>
            </Alert>
          ) : null}

          {connections.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : eligible.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {settings.delegated
                ? 'No other connection is available to take over.'
                : 'Nothing is connected to this zone right now. An agent has to be connected before it can be designated.'}
            </p>
          ) : (
            <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
              {eligible.map((connection) => (
                <li
                  key={connection.id}
                  className="flex flex-wrap items-center justify-between gap-3 bg-[var(--surface)] px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="font-mono text-sm text-foreground">{connection.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{connection.type}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={frozen}
                    onClick={() => {
                      setCandidate(connection)
                    }}
                  >
                    Make it the police
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <DesignateDialog
        zone={zone}
        target={candidate}
        onClose={() => {
          setCandidate(undefined)
        }}
      />
    </>
  )
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

function PoliceView({ zone }: { zone: ZoneSummary }) {
  const readOnly = useReadOnly()
  const me = useMe()
  const isZoneAdmin = useIsZoneAdministrator(zone)
  const police = usePolice(zone.id, isZoneAdmin)

  // `isZoneAdministrator` needs `/api/me` to answer, and answers `false` until it
  // does. Rendering the refusal in that window would tell the zone's own
  // administrator they are not it, for as long as the request takes.
  if (me.data === undefined) {
    return (
      <PageBody>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </PageBody>
    )
  }

  if (!isZoneAdmin) {
    return (
      <>
        <PageHeader eyebrow="Police" title={`Police of ${zone.name}`} />
        <PageBody>
          <EmptyState
            icon={ShieldAlertIcon}
            title="Only this zone's administrator can configure the police"
            description="Supervising a zone means holding a Claude API key and the power to silence any agent in it, so it is not an account-wide permission. An ADMIN on this account can make you the administrator of this zone from the Zones page."
          />
        </PageBody>
      </>
    )
  }

  return (
    <>
      <PageHeader
        eyebrow="Police"
        title={`Police of ${zone.name}`}
        description="A police watches this zone and can pause an agent that misbehaves. Either saltseed does the watching, against a prompt you write, or you hand the job to one of your own connected agents."
        actions={
          <Button
            variant="outline"
            onClick={() => {
              void police.refetch()
            }}
            disabled={police.isFetching}
          >
            <RefreshCwIcon className={cn('size-4', police.isFetching ? 'animate-spin' : undefined)} />
            Refresh
          </Button>
        }
      />

      <PageBody>
        <PausedZoneNotice zone={zone} />

        {police.isError ? (
          <ErrorState
            error={police.error}
            title="Could not load the police settings"
            onRetry={() => {
              void police.refetch()
            }}
          />
        ) : police.data === undefined ? (
          <>
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-96 w-full" />
          </>
        ) : (
          <>
            <SeatBanner settings={police.data} />
            <ConfigurationCard zone={zone} settings={police.data} frozen={!zone.reachable || readOnly} />
            <DelegationCard zone={zone} settings={police.data} frozen={!zone.reachable || readOnly} />

            <p className="text-xs text-muted-foreground">
              A paused agent stays connected and its tools keep answering — what stops is delivery,
              in both directions. Nothing is discarded until you say so: you settle both backlogs
              when you lift the pause, from the Connections page.
            </p>
          </>
        )}
      </PageBody>
    </>
  )
}

export function PolicePage() {
  return <RequireZone>{(zone) => <PoliceView zone={zone} />}</RequireZone>
}
